import { Component, OnInit } from "@angular/core";
import { DestroyBase } from "src/app/components/common/ng-destroy-base";
import { ProjectValuationDeleteService } from "../../../../modules/project-valuation/services/project-valuation-delete.service";
import { ComparablesDeleteService } from "../../../../modules/comparables/services/comparables.delete.service";
import { GenericPolluxResult } from "../../../../models/generic-pollux-result";
import { TranslateService } from "@ngx-translate/core";
import { EventBusService } from "../../../../services/event-bus.service";
import { Observable, forkJoin } from "rxjs";

/**
 * Confirm delete dialog for project valuation
 */
@Component({
  selector: 'confirm-delete-dialog',
  templateUrl: './confirm-delete-dialog.component.html',
  styleUrls: ['./confirm-delete-dialog.component.css']
})
export class ConfirmDeleteDialogComponent extends DestroyBase implements OnInit {
  /**
  * The dialog title.
  */
  dialogTitle: string;

  /**
  * The dialog message.
  */
  dialogMessage: string;

  /**
  * The type of delete triggered.
  */
  type: string;

  /**
  * The dialog message.
  */
  parameter: any;

  /**
  * Flag indicating if the confirmation dialog should be displayed
  */
  isDialogOpened = false;


  /**
   *OnInit
   * */
  ngOnInit(): void {
    this.subscribeToDeleteProjectEvent();
    this.subscribeToDeletePortfolioEvent();
    this.subscribeToDeleteReportEvent();
    this.subscribeToDeleteTenantEvent();
    this.subscribeToDeleteValuationEvent();
    this.subscribeToDeleteComparablesEvent();
  }

  /**
   * Constructor
   * @param ais
   * @param projectValuationDeleteService
   */
  constructor(private eventBus: EventBusService,
    private translate: TranslateService,
    private projectValuationDeleteService: ProjectValuationDeleteService,
    private comparablesDeleteService: ComparablesDeleteService) {
    super();
  }

  /**
   * Confirm event for dialog
   * */
  confirm() {
    this.delete(this.type, this.parameter);
  }

  /**
   * Cancel event for dialog
   * */
  cancel() {
    this.isDialogOpened = false;
    this.reset();
  }

  /**
  * Delete for a specific type
  * */
  delete(type: string, parameter: any) {
    switch (type) {
      case 'project':
        this.deleteProject(parameter);
        break;
      case 'portfolio':
        this.deletePortfolio(parameter);
        break;
      case 'report':
        this.deleteReport(parameter);
        break;
      case 'valuation':
        this.deleteValuation(parameter.scenario, parameter.oid);
        break;
      case 'tenant':
        this.deleteTenant(parameter);
        break;
      case 'comparablesProperty':
        this.deleteComparablesProperty(parameter);
        break;
      case 'comparablesLeaseTransaction':
        this.deleteComparablesLeaseTransaction(parameter);
        break;
      case 'comparablesValuation':
        this.deleteComparablesValuation(parameter);
        break;
      default:
        break;
    }
  }

  /**
  * Deletes project
  * */
  private deleteProject(parameter: any) {
    if (parameter.length === 1) {
      this.projectValuationDeleteService.deleteProject(parameter).subscribe(data => {
        this.isDialogOpened = false;
        this.reset();
        this.eventBus.broadcastEvent('ProjectstdLoadData');
      });
    } else {
      this.projectValuationDeleteService.deleteMultipleProjects(parameter).subscribe(data => {
        this.isDialogOpened = false;
        this.reset();
        this.eventBus.broadcastEvent('ProjectstdLoadData');
      });
    }
  }

  /** 
  * Deletes portfolio by oid or more by list of oids
  * */
  private deletePortfolio(parameter: any) {
    if (parameter.length > 0) {
      this.subscriptions.add(this.projectValuationDeleteService.deleteMultiplePortfolios(parameter).subscribe(data => {
        this.isDialogOpened = false;
        this.reset();
        this.eventBus.broadcastEvent('PortfolioLoadDataKeepFilter');
      }));
    }
    else {
      this.subscriptions.add(this.projectValuationDeleteService.deletePortfolio(parameter).subscribe(data => {
        this.isDialogOpened = false;
        this.reset();
        this.eventBus.broadcastEvent('PortfolioLoadDataKeepFilter');
      }));
    } 
  }

  /**
  * Deletes report by oid or more by list of oids
  * */
  private deleteReport(parameter: any) {
    if (parameter.length > 0)
      this.subscriptions.add(this.projectValuationDeleteService.deleteMultipleReports(parameter).subscribe(data => {
        this.isDialogOpened = false;
        this.reset();
        this.eventBus.broadcastEvent('reportDeleteLoadData');
        this.eventBus.broadcastEvent('setGridHeaderCheckboxToFalse');
      }));
    else this.subscriptions.add(this.projectValuationDeleteService.deleteReport(parameter).subscribe(data => {
      this.isDialogOpened = false;
      this.reset();
      this.eventBus.broadcastEvent('reportDeleteLoadData');
      this.eventBus.broadcastEvent('setGridHeaderCheckboxToFalse');
    }));
  }

  /**
  * Deletes tenant by oid or more by list of oids
  * */
  private deleteTenant(parameter: any) {
    if (parameter.length > 0) this.subscriptions.add(this.projectValuationDeleteService.deleteMultipleTenants(parameter).subscribe(data => {
      this.isDialogOpened = false;
      this.reset();
    }));
    else this.subscriptions.add(this.projectValuationDeleteService.deleteTenant(parameter).subscribe(data => {
      this.isDialogOpened = false;
      this.reset();
    }));
  }

  /**
  * Deletes valuation
  * */
  private deleteValuation(scenario: any, oid: any) {
    this.subscriptions.add(this.projectValuationDeleteService.deleteValuation(scenario, oid).subscribe(data => {
      this.isDialogOpened = false;
      this.reset();
    }));
  }

  /**
  * Delete project event
  * */
  private subscribeToDeleteProjectEvent() {
    this.eventBus.subscribeEvent('deleteProjectstd', (oidslist, _options) => {
      if (oidslist) {
        this.type = 'project';
        this.parameter = oidslist;
        this.isDialogOpened = true;
        this.dialogTitle = 'projectValuationConfirmDeleteProjectstdDialog.title';
        this.dialogMessage = 'projectValuationConfirmDeleteProjectstdDialog.messagePart1';
      }
    });
  }

  /**
  * Delete portfolio event
  * */
  private subscribeToDeletePortfolioEvent() {
    this.eventBus.subscribeEvent('deletePortfolios', (oidslist: number[], _options) => {
      if (oidslist) {
        const observables: Observable<any>[] = [];
        this.parameter = oidslist;
        for (const oid of oidslist) {
          observables.push(this.projectValuationDeleteService.canDeletePortfolio(oid));
        }

        forkJoin(observables).subscribe((data: any[]) => {
          this.isDialogOpened = true;
          if (data.find((result) => { return result.hasLinkedData; })) {
            this.dialogTitle = '';
            this.dialogMessage = 'projectValuationConfirmDeletePortfolioDialog.deleteMessageWithLinkedDataPrevent';
          }
          else {
            this.type = 'portfolio';
            this.dialogTitle = 'projectValuationConfirmDeletePortfolioDialog.title';
            this.dialogMessage = 'projectValuationConfirmDeletePortfolioDialog.deleteMessageNoLinkedData';
          }
        });
      }
    });
  }

  /**
  * Delete valuation event
  * */
  private subscribeToDeleteValuationEvent() {
    this.eventBus.subscribeEvent('deleteValuation', (oidslist, _options) => {
      if (oidslist) {
        this.parameter = oidslist;
        this.isDialogOpened = true;
        this.type = 'valuation';
        this.dialogTitle = 'projectValuationConfirmDeleteValuationDialog.title';
        this.dialogMessage = 'projectValuationConfirmDeleteValuationDialog.messagePart1' + ` ${this.parameter} ` + 'projectValuationConfirmDeleteValuationDialog.messagePart2';
      }
    });
  }

  /**
  * Delete report event
  * */
  private subscribeToDeleteReportEvent() {
    this.eventBus.subscribeEvent('deleteReports', (oidslist, _options) => {
      if (oidslist) {
        this.type = 'report';
        this.parameter = oidslist;
        this.dialogMessage = this.translate.instant('projectValuationConfirmDeleteReportDialog.messagePart1') + ` ${this.parameter.length} ` + this.translate.instant('projectValuationConfirmDeleteReportDialog.messagePart2') + ' ';
        this.dialogTitle = 'projectValuationConfirmDeleteReportDialog.title';
        this.isDialogOpened = true;
      }
    });
  }

  /**
  * Delete tenant event
  * */
  private subscribeToDeleteTenantEvent() {
    this.eventBus.subscribeEvent('deleteTenant', (oidslist, _options) => {
      if (oidslist) {
        this.isDialogOpened = true;
        this.parameter = oidslist;
        this.type = 'tenant';
        this.dialogTitle = 'projectValuationConfirmDeleteTenantDialog.title';
        this.dialogMessage = 'projectValuationConfirmDeleteTenantDialog.messagePart1' + ` ${this.parameter} ` + 'projectValuationConfirmDeleteTenantDialog.messagePart2';
      }
    });
  }

  /**
  * Listen for deleteComparablesConfirmation event
  * */
  private subscribeToDeleteComparablesEvent(): void {
    this.eventBus.subscribeEvent('deleteComparablesConfirmation', (oidslist, options) => {
      if (oidslist) {
        this.parameter = oidslist;
        this.type = options.type;
        this.dialogTitle = this.translate.instant('comparables.DeleteConfirmationLabel');//won't be shown because of css, but anyway
        this.dialogMessage = 'comparables.DeleteConfirmationTextPart1';

        if (this.type === 'comparablesProperty') {
          this.comparablesDeleteService.canDeleteComparablesProperties(oidslist).then((canDelete) => {
            if (canDelete) {
              this.isDialogOpened = true;
            }
            else {
              this.eventBus.broadcastEvent('openConfirmDialog', {
                success: () => {
                  //Nothing
                }
              }, 'comparables.deletePropertiesWithLinkedData');
            }
          });
        }
      }
    });
  }

  /**
  * Deletes comparables properties by list of oids
  * */
  private async deleteComparablesProperty(parameter: number[]) {
    const result: GenericPolluxResult = await this.comparablesDeleteService.deleteComparablesProperties(parameter);
    if (!result.success) {
      let message = result.message;

      //when deleting properties, warn user to delete linked data first
      if (message.startsWith("removeLinkedDataFromProperty")) {
        const oid = message.split("#")[1];
        message = this.translate.instant('removeLinkedDataFromProperty') + oid;
      }

      alert(message);
    }

    this.isDialogOpened = false;
    this.reset();
    this.eventBus.broadcastEvent('properties.loadData');
  }

  /**
  * Deletes comparables lease transactions by list of oids
  * */
  private async deleteComparablesLeaseTransaction(parameter: number[]) {
    const result: GenericPolluxResult = await this.comparablesDeleteService.deleteComparablesLeaseTransactions(parameter);
    if (!result.success) {
      const message = result.message;
      alert(message);
    }

    this.isDialogOpened = false;
    this.reset();
    this.eventBus.broadcastEvent('leases.loadData');
  }

  /**
  * Deletes comparables valuations by list of oids
  * */
  private async deleteComparablesValuation(parameter: number[]) {
    const result: GenericPolluxResult = await this.comparablesDeleteService.deleteComparablesValuations(parameter);
    if (!result.success) {
      const message = result.message;
      alert(message);
    }

    this.isDialogOpened = false;
    this.reset();
    this.eventBus.broadcastEvent('valuations.loadData');
  }

  /**
  * Reset dialog message and title
  * */
  private reset(): void {
    this.dialogMessage = '';
    this.dialogTitle = '';
  }
}
